import { IAccountDocument } from '@epicuro-next/data/documents';
import { IUser } from '@epicuro-next/data/users';
import { createReducer, on } from '@ngrx/store';

import {
  acceptUserTerms,
  acceptUserTermsFail,
  acceptUserTermsSuccess,
  changePassword,
  changePasswordFail,
  changePasswordFormOpened,
  changePasswordSuccess,
  loadPrivacyStatementFailure,
  loadPrivacyStatementSuccess,
  loadTradeTermsFailure,
  loadTradeTermsSuccess,
  loadUser,
  loadUserAgreementFailure,
  loadUserAgreementSuccess,
  loadUserFailure,
  loadUserSuccess,
  loginSuccess,
  logout,
  postUserPreferences,
  postUserPreferencesFailure,
  postUserPreferencesSuccess,
  setAdvancedMDOMode,
  setDarkMode,
  setPreferredScaleSet,
  setPreventZoom,
  setShowWholesalePrices,
  setSubscribeToAllEmailsSuccess,
  setUnsignedUserTerms,
  uploadAvatarImage,
  uploadAvatarImageFail,
  uploadAvatarImageSuccess,
} from './actions';

export const userFeatureKey = 'user';

export interface State {
  current: IUser | null;
  loading: boolean;
  loggedId: boolean;
  error: string | null;
  changingPassword: boolean;
  updatingPreferences: boolean;
  advancedMDOEnabled?: boolean;
  changePasswordErrors?: ReadonlyArray<string>;
  showWholeSalePrices?: boolean;
  darkMode?: boolean;
  preventZoom?: boolean;
  unsignedUserTerms?: ReadonlyArray<IAccountDocument>;
  uploadingAvatar: boolean;
  acceptingUserTerm: boolean;
  privacyStatements?: ReadonlyArray<IAccountDocument>;
  tradeTerms?: ReadonlyArray<IAccountDocument>;
  userAgreements?: ReadonlyArray<IAccountDocument>;
}

export const initialState: State = {
  loading: false,
  loggedId: false,
  error: null,
  changingPassword: false,
  updatingPreferences: false,
  uploadingAvatar: false,
  acceptingUserTerm: false,
  current: null,
  darkMode: undefined,
  preventZoom: true,
  showWholeSalePrices: true,
  advancedMDOEnabled: true,
};

export const storageKeys: (keyof State)[] = [
  'showWholeSalePrices',
  'preventZoom',
  'darkMode',
  'advancedMDOEnabled',
];

export const reducer = createReducer(
  initialState,
  on(loadUser, (state) => ({ ...state, loading: true })),
  on(loginSuccess, (state) => ({ ...state, loggedId: true })),
  on(loadUserSuccess, (state, { user }) => ({
    ...state,
    current: user,
    loading: false,
  })),
  on(loadUserFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(changePassword, (state) => ({
    ...state,
    changingPassword: true,
    changePasswordError: undefined,
  })),
  on(changePasswordSuccess, changePasswordFail, (state) => ({
    ...state,
    changingPassword: false,
  })),
  on(changePasswordFail, (state, { errors }) => ({
    ...state,
    changingPassword: false,
    changePasswordErrors: errors,
  })),
  on(changePasswordFormOpened, (state) => ({
    ...state,
    changePasswordError: undefined,
  })),
  on(setShowWholesalePrices, (state, { show }) => ({
    ...state,
    showWholeSalePrices: show,
  })),
  on(
    setAdvancedMDOMode,
    (state, { advancedMDOMode }): State => ({
      ...state,
      advancedMDOEnabled: advancedMDOMode,
    }),
  ),
  on(setDarkMode, (state, { darkModeOn }) => ({
    ...state,
    darkMode: darkModeOn,
  })),
  on(setPreventZoom, (state, { preventZoom }) => ({
    ...state,
    preventZoom,
  })),
  on(setUnsignedUserTerms, (state, { documents }) => ({
    ...state,
    unsignedUserTerms: documents,
  })),
  on(uploadAvatarImage, (state) => ({ ...state, uploadingAvatar: true })),
  on(uploadAvatarImageSuccess, (state, { url }) => ({
    ...state,
    current: state.current ? { ...state.current, imageLink: url } : null,
    uploadingAvatar: false,
  })),
  on(uploadAvatarImageFail, (state) => ({ ...state, uploadingAvatar: false })),
  on(acceptUserTerms, (state) => ({ ...state, acceptingUserTerm: true })),
  on(acceptUserTermsSuccess, (state, { document }) => ({
    ...state,
    unsignedUserTerms: state.unsignedUserTerms?.filter(
      ({ id }) => id !== document.id,
    ),
    acceptingUserTerm: false,
  })),
  on(acceptUserTermsFail, (state) => ({ ...state, acceptingUserTerm: false })),
  on(setPreferredScaleSet, (state: State, { scaleSetId }) => {
    const current = state.current ? { ...state.current } : null;
    if (current?.preferences) {
      current.preferences = {
        ...current.preferences,
        preferredScaleSet: scaleSetId,
      };
    }
    return { ...state, current };
  }),
  on(postUserPreferences, (state) => ({
    ...state,
    updatingPreferences: true,
  })),
  on(postUserPreferencesFailure, (state) => ({
    ...state,
    updatingPreferences: false,
  })),
  on(postUserPreferencesSuccess, (state, { preferences }) => ({
    ...state,
    updatingPreferences: false,
    current: state.current ? { ...state.current, preferences } : null,
  })),
  on(loadPrivacyStatementSuccess, (state, { documents }) => ({
    ...state,
    privacyStatements: documents,
  })),
  on(loadPrivacyStatementFailure, (state) => ({
    ...state,
    privacyStatements: [],
  })),
  on(loadUserAgreementSuccess, (state, { documents }) => ({
    ...state,
    userAgreements: documents,
  })),
  on(loadUserAgreementFailure, (state) => ({
    ...state,
    userAgreements: [],
  })),
  on(loadTradeTermsSuccess, (state, { documents }) => ({
    ...state,
    tradeTerms: documents,
  })),
  on(loadTradeTermsFailure, (state) => ({
    ...state,
    tradeTerms: [],
  })),
  on(
    setSubscribeToAllEmailsSuccess,
    (state, { isSubscribedToAccountBaskets }) => ({
      ...state,
      isSubscribedToAccountBaskets,
    }),
  ),
  on(logout, () => initialState),
);
